// composables/directusRetry.ts
// ---------------------------------------------------------------------------
// Deux defauts distincts, traites ensemble :
//
// 1. Directus renvoie des 503 « Under pressure » intermittents (pressure limiter,
//    instance saturee). -> on reessaie avant d'abandonner.
// 2. Partout dans l'app, un `catch` generique fait `router.push('/login')` : un
//    503, une coupure reseau ou un bug JS deconnectent un utilisateur dont la
//    session est parfaitement valide. -> on ne redirige que sur un VRAI 401/403.
//
// ATTENTION : `withRetry` ne doit jamais envelopper une ECRITURE. Si Directus a
// cree l'item mais que la reponse s'est perdue, le retry creerait un doublon.
// Jumeau cote requetes paginees : composables/paginateAll.ts (meme backoff).
// ---------------------------------------------------------------------------

/** Vrai uniquement pour une erreur d'authentification/autorisation Directus. */
export function isAuthError(e: any): boolean {
  const status = e?.response?.status ?? e?.statusCode ?? e?.status
  return status === 401 || status === 403
}

/** Rejoue `fn` sur erreur transitoire (503, reseau). Rethrow immediat si 401/403. */
export async function withRetry<T>(fn: () => Promise<T>, maxRetries = 3): Promise<T> {
  let lastErr: unknown
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (e) {
      lastErr = e
      if (isAuthError(e)) throw e   // reessayer ne resoudra pas une erreur d'auth
      if (attempt < maxRetries) {
        await new Promise((r) => setTimeout(r, 500 * attempt))
      }
    }
  }
  throw lastErr
}
